import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Mail, Printer, Check, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { useSettings } from "../context/SettingsContext";
import { artistSchema, breadcrumbSchema, GENRES } from "../lib/schema";
import Seo from "../components/Seo";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { SpotifyIcon, YouTubeIcon } from "../components/icons/BrandIcons";

const card = "rounded-3xl border border-white/10 bg-surface/60 p-6";
const h2 = "mb-4 text-[11px] font-bold uppercase tracking-[0.3em] text-gold";

function BookingForm({ email }) {
  const [f, setF] = useState({ name: "", email: "", type: "booking", organization: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await api.post("/inquiries", f);
      track("booking_submit", { metadata: { type: f.type } });
      setStatus("done");
    } catch { setStatus("error"); }
  };
  const inp = "mt-1 w-full rounded-xl border border-white/15 bg-ink/60 px-4 py-3 text-sm text-white outline-none focus:border-gold";
  if (status === "done") return <div data-testid="booking-success" className="flex items-center gap-3 rounded-2xl border border-gold/30 bg-gold/10 px-5 py-4 text-gold"><Check size={18} /> <span className="text-sm font-semibold">Received. DJ Código's team will reply shortly.</span></div>;
  return (
    <form onSubmit={submit} data-testid="booking-form" className="grid gap-3 sm:grid-cols-2">
      <label className="text-[11px] uppercase tracking-widest text-white/40">Name<input data-testid="booking-name" required value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} /></label>
      <label className="text-[11px] uppercase tracking-widest text-white/40">Email<input data-testid="booking-email" required type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inp} /></label>
      <label className="text-[11px] uppercase tracking-widest text-white/40">Inquiry type
        <select data-testid="booking-type" value={f.type} onChange={(e) => set("type", e.target.value)} className={inp}>
          {["booking", "press", "collaboration", "licensing", "playlist", "other"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label className="text-[11px] uppercase tracking-widest text-white/40">Organization / outlet<input value={f.organization} onChange={(e) => set("organization", e.target.value)} className={inp} /></label>
      <input type="text" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <label className="text-[11px] uppercase tracking-widest text-white/40 sm:col-span-2">Message<textarea data-testid="booking-message" required rows={4} value={f.message} onChange={(e) => set("message", e.target.value)} className={inp} /></label>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        <button data-testid="booking-submit" disabled={status === "loading"} className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-black disabled:opacity-60"><Mail size={16} /> Send inquiry</button>
        {email && <a href={`mailto:${email}`} className="text-xs text-white/50 hover:text-gold">or email {email}</a>}
        {status === "error" && <span className="text-xs text-red-300">Something went wrong — try again.</span>}
      </div>
    </form>
  );
}

export default function Press() {
  const settings = useSettings();
  const [releases, setReleases] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [videos, setVideos] = useState([]);
  useEffect(() => {
    window.scrollTo(0, 0);
    track("press_epk_view", {});
    Promise.all([api.get("/releases"), api.get("/releases/featured"), api.get("/videos")])
      .then(([r, f, v]) => { setReleases(r.data || []); setFeatured(f.data); setVideos((v.data || []).slice(0, 3)); }).catch(() => {});
  }, []);
  const s = settings || {};
  const press = s.press || {};
  const m = s.metrics || {};
  const streaming = s.streaming || {};
  const socials = s.socials || {};
  const photos = [s.artistImage, ...(press.pressPhotos || [])].filter(Boolean);
  const dl = (url) => track("press_asset_download", { metadata: { url } });

  return (
    <div className="grain relative min-h-screen pb-32 print:bg-white">
      <Seo
        title="Press Kit / EPK — Bio, Photos, Music, Booking"
        description={`Official electronic press kit for DJ Código (DJ Codigo): bio, press photos, top releases, streaming links, videos and booking / licensing contact. ${GENRES.join(" • ")}.`}
        path="/press"
        type="profile"
        jsonLd={[artistSchema(settings), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Press / EPK", path: "/press" }], settings)]}
      />
      <div className="px-6 pt-28 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.35em] text-gold">Electronic Press Kit</p>
              <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">DJ Código</h1>
              <p className="mt-2 font-display text-lg text-white/80 sm:text-2xl">{s.tagline}</p>
              <p className="mt-1 text-sm text-white/50">{s.genreLine} • {press.hometown || "Dominican Republic"}</p>
            </div>
            <div className="flex gap-2 print:hidden">
              <button data-testid="epk-print" onClick={() => { track("press_epk_download", {}); window.print(); }} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-xs font-bold uppercase tracking-wide text-black"><Printer size={14} /> Save EPK as PDF</button>
              <a href="#contact" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-white/80 hover:border-gold/50 hover:text-gold"><Mail size={14} /> Contact</a>
            </div>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            <div className={`${card} lg:col-span-2`} data-testid="epk-bio">
              <h2 className={h2}>Biography</h2>
              <p className="text-base leading-relaxed text-white/85">{s.bioShort}</p>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-white/60">{(s.bioFull || "").split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}</div>
              {press.pullQuote && <blockquote className="mt-6 border-l-2 border-gold pl-4 font-display text-lg italic text-white/80">“{press.pullQuote}”</blockquote>}
            </div>
            <div className="space-y-6">
              <div className={card} data-testid="epk-facts">
                <h2 className={h2}>Quick facts</h2>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4 border-b border-white/5 pb-2"><dt className="text-white/50">Artist</dt><dd className="text-white">DJ Código (DJ Codigo)</dd></div>
                  <div className="flex justify-between gap-4 border-b border-white/5 pb-2"><dt className="text-white/50">Genres</dt><dd className="text-right text-white">{GENRES.join(", ")}</dd></div>
                  <div className="flex justify-between gap-4 border-b border-white/5 pb-2"><dt className="text-white/50">Roots</dt><dd className="text-white">{press.hometown || "Dominican Republic"}</dd></div>
                  <div className="flex justify-between gap-4 border-b border-white/5 pb-2"><dt className="text-white/50">Status</dt><dd className="text-white">Independent</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-white/50">Releases</dt><dd className="text-white">{releases.length}</dd></div>
                </dl>
              </div>
              {(m.spotifyMonthlyListeners || m.youtubeSubscribers || m.totalStreams) && (
                <div className={card} data-testid="epk-metrics">
                  <h2 className={h2}>Streaming metrics</h2>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {[["Spotify monthly", m.spotifyMonthlyListeners], ["YouTube subs", m.youtubeSubscribers], ["Streams", m.totalStreams]].filter(([, v]) => v).map(([l, v]) => (
                      <div key={l}><p className="font-display text-2xl font-bold text-gold">{v}</p><p className="text-[10px] uppercase tracking-widest text-white/50">{l}</p></div>
                    ))}
                  </div>
                  {m.updatedAt && <p className="mt-2 text-[10px] text-white/30">Updated {m.updatedAt}</p>}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className={card} data-testid="epk-links">
              <h2 className={h2}>Listen & follow (official)</h2>
              <div className="flex flex-wrap gap-2">
                {streaming.spotify && <a href={streaming.spotify} target="_blank" rel="noreferrer" onClick={() => track("spotify_click", { metadata: { placement: "epk" } })} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold uppercase text-white/85"><SpotifyIcon size={14} /> Spotify</a>}
                {streaming.apple_music && <a href={streaming.apple_music} target="_blank" rel="noreferrer" onClick={() => track("apple_music_click", { metadata: { placement: "epk" } })} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold uppercase text-white/85">Apple Music</a>}
                {streaming.youtube_music && <a href={streaming.youtube_music} target="_blank" rel="noreferrer" onClick={() => track("youtube_music_click", { metadata: { placement: "epk" } })} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold uppercase text-white/85"><YouTubeIcon size={14} /> YouTube Music</a>}
                {Object.entries(socials).filter(([, v]) => v).map(([k, v]) => (
                  <a key={k} href={v} target="_blank" rel="noreferrer" onClick={() => track(`${k}_click`, { metadata: { placement: "epk" } })} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold uppercase capitalize text-white/85">{k} <ExternalLink size={11} /></a>
                ))}
              </div>
            </div>
            <div className={card} data-testid="epk-current">
              <h2 className={h2}>Current release</h2>
              {featured && (
                <div className="flex items-center gap-4">
                  <img src={featured.artwork} alt={`${featured.title} cover`} className="h-24 w-24 rounded-xl border border-white/10 object-cover" />
                  <div className="min-w-0">
                    <p className="font-display text-xl font-semibold text-white">{featured.title}</p>
                    <p className="text-xs uppercase tracking-widest text-white/40">{featured.releaseType} • {featured.releaseDate}</p>
                    <Link to={`/music/${featured.slug}`} className="mt-2 inline-block text-xs font-semibold uppercase tracking-widest text-gold">Release page →</Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={`${card} mt-6`} data-testid="epk-photos">
            <h2 className={h2}>Press photos & logo</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {press.logo && <a href={press.logo} download target="_blank" rel="noreferrer" onClick={() => dl(press.logo)} className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10 bg-black/40 p-4"><img src={press.logo} alt="DJ Código logo" className="h-full w-full object-contain" /><span className="absolute bottom-2 right-2 rounded-full bg-black/60 p-1.5 text-white/80"><Download size={12} /></span></a>}
              {photos.map((p, i) => (
                <a key={i} href={p} download target="_blank" rel="noreferrer" onClick={() => dl(p)} data-testid={`press-photo-${i}`} className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10"><img src={p} alt={`DJ Código press photo ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" /><span className="absolute bottom-2 right-2 rounded-full bg-black/60 p-1.5 text-white/80"><Download size={12} /></span></a>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-white/30">Click any image to open the full-resolution file. Approved for editorial use with credit “DJ Código”.</p>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className={card} data-testid="epk-releases">
              <h2 className={h2}>Top releases</h2>
              <ul className="divide-y divide-white/5">
                {releases.slice(0, 6).map((r) => (
                  <li key={r.internalId} className="flex items-center gap-3 py-2.5">
                    <img src={r.artwork} alt="" className="h-10 w-10 rounded-md object-cover" />
                    <Link to={`/music/${r.slug}`} className="min-w-0 flex-1 truncate text-sm font-semibold text-white hover:text-gold">{r.title}</Link>
                    <span className="text-[10px] uppercase tracking-widest text-white/40">{r.releaseType} • {r.releaseDate?.slice(0, 4)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={card} data-testid="epk-videos">
              <h2 className={h2}>Music videos</h2>
              <ul className="divide-y divide-white/5">
                {videos.map((v) => (
                  <li key={v.youtubeId} className="flex items-center gap-3 py-2.5">
                    <img src={v.thumbnail} alt="" className="h-10 w-16 rounded-md object-cover" />
                    <Link to={`/videos/${v.slug}`} className="min-w-0 flex-1 truncate text-sm font-semibold text-white hover:text-gold">{v.title}</Link>
                  </li>
                ))}
                {videos.length === 0 && <li className="py-2 text-sm text-white/40">Videos coming soon.</li>}
              </ul>
              <Link to="/videos" className="mt-3 inline-block text-xs font-semibold uppercase tracking-widest text-gold">All videos →</Link>
            </div>
          </div>

          <div id="contact" className={`${card} mt-6 border-gold/30 print:hidden`} data-testid="epk-contact">
            <h2 className={h2}>Booking • Press • Collaboration • Licensing</h2>
            <p className="mb-5 max-w-2xl text-sm text-white/60">{press.licensingNote || s.booking?.note}</p>
            <BookingForm email={s.booking?.email} />
          </div>
        </div>
      </div>
      <Footer settings={settings} />
    </div>
  );
}
